import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Star, Zap } from "lucide-react";

export default function DigtekHero() {
    const heroRef = useRef(null);
    const [sigKey, setSigKey] = useState(0);

    // Replay the signature scribble each time the hero re-enters the viewport
    useEffect(() => {
        const el = heroRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        let wasIn = true; // draws on initial mount
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !wasIn) {
                        wasIn = true;
                        setSigKey((k) => k + 1);
                    } else if (!entry.isIntersecting) {
                        wasIn = false;
                    }
                });
            },
            { threshold: 0.35 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <section
            ref={heroRef}
            data-testid="digtek-hero"
            id="top"
            className="relative pt-40 pb-24 lg:pt-48 lg:pb-32 overflow-hidden bg-digtek-bg"
        >
            {/* Decorative blobs */}
            <div className="digtek-blob purple w-[500px] h-[500px] -left-40 -top-32" />
            <div className="digtek-blob lime w-[420px] h-[420px] right-[10%] top-1/3 opacity-70" />

            {/* Floating star shapes */}
            <div className="absolute top-32 right-[15%] animate-wiggle z-10">
                <Star className="w-8 h-8 text-digtek-lime fill-digtek-lime" />
            </div>
            <div className="absolute top-64 left-[12%] animate-spin-slow z-10">
                <Star className="w-6 h-6 text-digtek-purple fill-digtek-purple" />
            </div>
            <div className="absolute bottom-40 right-[38%] animate-wiggle z-10" style={{ animationDelay: '.4s' }}>
                <Zap className="w-7 h-7 text-digtek-purple fill-digtek-purple" />
            </div>

            {/* Dotted ring background */}
            <div
                aria-hidden
                className="absolute -bottom-24 -right-24 w-[420px] h-[420px] digtek-dot-ring opacity-40 rounded-full"
            />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                {/* Left column */}
                <div>
                    <div className="digtek-eyebrow mb-6">
                        <span data-testid="hero-eyebrow">
                            Go for growth
                        </span>
                    </div>
                    <h1
                        data-testid="hero-heading"
                        className="font-display font-bold leading-[0.95] tracking-tight text-[52px] sm:text-[72px] lg:text-[92px] text-digtek-header"
                    >
                        Think{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10">digital</span>
                            <span
                                aria-hidden
                                className="absolute inset-x-0 bottom-2 h-4 bg-digtek-lime -z-0"
                            />
                        </span>
                        <br />
                        move{" "}
                        <span className="relative inline-block">
                            <em className="not-italic text-digtek-purple">bold</em>
                            {/* Hand-drawn signature scribble under "bold" */}
                            <svg
                                key={sigKey}
                                aria-hidden
                                viewBox="0 0 260 60"
                                className="digtek-signature-scribble absolute -bottom-6 left-0 w-full h-8 overflow-visible"
                                preserveAspectRatio="none"
                            >
                                <path
                                    d="M6 28 C 40 8, 90 46, 130 22 S 220 6, 254 26"
                                    fill="none"
                                    stroke="#5B3FE0"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeDasharray="600"
                                    strokeDashoffset="600"
                                    className="digtek-signature-path"
                                />
                                <path
                                    d="M212 40 L246 26 M232 42 L246 26 M246 26 L240 12"
                                    fill="none"
                                    stroke="#5B3FE0"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray="120"
                                    strokeDashoffset="120"
                                    className="digtek-signature-arrow"
                                />
                            </svg>
                        </span>
                        <span className="text-digtek-purple">.</span>
                    </h1>
                    <p
                        data-testid="hero-subhead"
                        className="mt-8 text-digtek-text text-lg leading-[1.65] max-w-[540px]"
                    >
                        Aximo is the growth studio behind the brands you'll be
                        Googling next year. Strategy, content, paid, and
                        engineered digital — one team, one operating rhythm,
                        results you can measure by Friday.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            href="#contact"
                            data-testid="hero-cta-primary"
                            className="btn-digtek"
                        >
                            Start a project
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                        <a
                            href="#work"
                            data-testid="hero-cta-secondary"
                            className="btn-digtek-outline"
                        >
                            See our work
                        </a>
                    </div>

                    {/* Reviews strip */}
                    <div className="mt-12 flex items-center gap-4">
                        <div className="flex -space-x-3">
                            {[
                                "https://i.pravatar.cc/64?img=32",
                                "https://i.pravatar.cc/64?img=12",
                                "https://i.pravatar.cc/64?img=68",
                                "https://i.pravatar.cc/64?img=47",
                            ].map((src) => (
                                <img
                                    key={src}
                                    src={src}
                                    alt=""
                                    className="w-11 h-11 rounded-full ring-4 ring-digtek-bg object-cover"
                                />
                            ))}
                        </div>
                        <div>
                            <div className="flex items-center gap-0.5 text-digtek-lime">
                                {["s1", "s2", "s3", "s4", "s5"].map((sk) => (
                                    <Star
                                        key={sk}
                                        className="w-4 h-4 fill-current"
                                        style={{ color: "#FFB800" }}
                                    />
                                ))}
                            </div>
                            <p className="text-[13px] font-semibold text-digtek-header mt-1">
                                450+ founders trust Aximo
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right column — hero character illustration */}
                <div className="relative flex items-center justify-center">
                    <div className="relative w-full max-w-[540px] aspect-square">
                        {/* Radial gradient circle backdrop — lime core, deep purple rim */}
                        <div
                            aria-hidden
                            className="absolute inset-8 rounded-full digtek-hero-orb"
                        />
                        {/* Rotating dashed ring */}
                        <div
                            aria-hidden
                            className="absolute inset-0 rounded-full border-2 border-dashed border-digtek-header animate-spin-slow"
                        />
                        {/* Cinematic key + fill + rim lighting behind the character */}
                        <div
                            aria-hidden
                            className="absolute inset-8 rounded-full digtek-hero-glow pointer-events-none"
                        />
                        {/* Photographic grain for premium depth */}
                        <div
                            aria-hidden
                            className="absolute inset-8 rounded-full digtek-hero-grain pointer-events-none"
                        />

                        {/* Superhero character — bursts out of the circle */}
                        <img
                            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_agency-portal-93/artifacts/na14d5ox_hero-image.png"
                            alt="Aximo growth superhero"
                            data-testid="hero-character"
                            className="digtek-hero-character absolute inset-0 w-full h-full object-contain object-bottom drop-shadow-[0_40px_60px_rgba(23,1,44,0.35)] z-10"
                        />

                        {/* Floating 3D rocket — top-right */}
                        <img
                            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_agency-portal-93/artifacts/xdz1jad6_rocket.png"
                            alt=""
                            aria-hidden
                            data-testid="hero-rocket-float"
                            className="digtek-float-emoji digtek-float-rocket absolute -top-2 -right-2 w-28 h-28 z-20 drop-shadow-[0_20px_40px_rgba(23,1,44,0.35)]"
                        />

                        {/* Floating stat card */}
                        <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl px-5 py-4 shadow-[0_20px_50px_-10px_rgba(23,1,44,.2)] flex items-center gap-4 z-20">
                            <div className="w-12 h-12 rounded-full bg-digtek-purple text-digtek-lime flex items-center justify-center">
                                <Zap className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-2xl font-display font-bold text-digtek-header leading-none">
                                    +217%
                                </p>
                                <p className="text-xs text-digtek-text font-medium mt-1">
                                    avg. traffic growth in 90 days
                                </p>
                            </div>
                        </div>

                        {/* Rotating "the best solution" circle */}
                        <div className="absolute -bottom-8 right-4 w-28 h-28 animate-spin-slow z-20">
                            <svg viewBox="0 0 200 200" className="w-full h-full">
                                <defs>
                                    <path
                                        id="circle-text"
                                        d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0"
                                    />
                                </defs>
                                <text
                                    fill="#17012C"
                                    fontSize="18"
                                    fontFamily="Space Grotesk"
                                    fontWeight="700"
                                    letterSpacing="4"
                                >
                                    <textPath href="#circle-text">
                                        THE BEST SOLUTION · GROW WITH AXIMO ·
                                    </textPath>
                                </text>
                            </svg>
                            <div className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-digtek-lime flex items-center justify-center">
                                <Star className="w-4 h-4 text-digtek-header fill-digtek-header" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
